// frontend/src/hooks/useWebSocket.js
import { useEffect, useRef, useCallback } from "react";
import { useWebSocketContext } from "../contexts/WebSocketContext.jsx";

/**
 * Suscribe callbacks al WebSocket compartido (gestionado por WebSocketProvider).
 * No abre conexiones propias — solo subscribe/unsubscribe en el contexto.
 *
 * @param {(msg: object) => void} onMessage  Callback para mensajes recibidos
 * @param {{ onReconnect?: (reason?: "reconnect" | "tab-return") => void }} options
 *   `onReconnect` recibe el motivo del resync: "reconnect" (el socket se cayó
 *   y volvió) o "tab-return" (la pestaña volvió tras una ausencia larga con el
 *   socket sano). Ambos resyncs corren en silencio (sin overlay); el motivo es
 *   informativo — hoy solo alimenta los logs `[ws-resync]`.
 * @returns {{ sendMessage: (msg: object) => void, connected: boolean }}
 */
export function useWebSocket(onMessage, { onReconnect } = {}) {
  const ctx = useWebSocketContext();

  // Refs estables para evitar re-suscripciones en cada render
  const onMessageRef = useRef(onMessage);
  const onReconnectRef = useRef(onReconnect);
  onMessageRef.current = onMessage;
  onReconnectRef.current = onReconnect;

  // Funciones de callback con identidad estable (misma referencia entre renders)
  const stableHandler = useRef((msg) => onMessageRef.current?.(msg));
  const stableReconnect = useRef((reason) => onReconnectRef.current?.(reason));

  useEffect(() => {
    ctx.subscribe(stableHandler.current);
    return () => ctx.unsubscribe(stableHandler.current);
  }, [ctx]);

  useEffect(() => {
    if (!onReconnect) return;
    ctx.onReconnect(stableReconnect.current);
    return () => ctx.offReconnect(stableReconnect.current);
  }, [ctx, onReconnect]);

  const sendMessage = useCallback((msg) => ctx.sendMessage(msg), [ctx]);

  return {
    sendMessage,
    connected: ctx.connected,
    getAndConsumeLastAbsenceMs: ctx.getAndConsumeLastAbsenceMs,
    isSocketOpen: ctx.isSocketOpen,
  };
}
